import {Route, Routes, useParams} from "react-router-dom";
import {useEffect, useState} from "react";
import {InitInfo} from "./InitInfo/InitInfo";
import {DealsInfo} from "./DealsInfo/DealsInfo";
import {OthersInfo} from "./OthersInfo/OthersInfo";
import {DrawingsInfo} from "./DrawingsInfo/DrawingsInfo";
import baseUrl from "../../baseUrl";
import {DealNew} from "./Navigation/DealNew/DealNew";
import {OtherNew} from "./Navigation/OtherNew/OtherNew";
import {BpRoadsForm} from "./Navigation/OtherNew/BpRoadsForm";
import {DrawingNew} from "./Navigation/DrawingNew/DrawingNew";
import {CompanyNew} from "./Navigation/CompanyNew/CompanyNew";
import {CopyFromPreviousYear} from "./Navigation/CopyFromPreviousYear/CopyFromPreviousYear";
import {ForDrawingCard} from "./ForDrawingCard/ForDrawingCard";
import {DrawingsSumCard} from "./DrawingsSumCard/DrawingsSumCard";
import {CompanyInfoModal} from "../CompanyInfoModal/CompanyInfoModal";
import {requester} from "../../services/requester";
import {RouteGuard} from "../common/RouteGuard";
import {useNavContext} from "../../contexts/NavContext";
import {settlementLabel} from "../../utils/settlements";
import {pravno} from "../../utils/pravno";
import styles from "./CompanyInfo.module.css";


export const CompanyInfo = () => {
    const {year, settlement_ekatte, bullstat} = useParams();
    const {setTabMarker} = useNavContext();
    const [initInfo, setInitInfo] = useState({});
    const [dealsInfo, setDealsInfo] = useState([]);
    const [othersInfo, setOthersInfo] = useState({});
    const [drawInfo, setDrawInfo] = useState([]);
    const [companyData, setCompanyData] = useState({});
    const [companyTitle, setCompanyTitle] = useState("");
    const [settlementTitle, setSettlementTitle] = useState("");
    const [sumDrawings, setSumDrawings] = useState(0);
    const [sumDeals, setSumDeals] = useState(0);
    const [sumToDrawing, setSumToDrawing] = useState(0);
    const [cropsOptions, setCropsOptions] = useState([]);
    // Щ5: ползвателите в ТОВА землище — за прехвърляне на изчертан ред.
    const [settlementCompanies, setSettlementCompanies] = useState([]);
    const request = requester();

    useEffect(() => {
        Promise.all([
            request.get(`${baseUrl()}/companies-initial-info/${year}/${settlement_ekatte}/${bullstat}`),
            request.get(`${baseUrl()}/deals/${year}/${settlement_ekatte}/${bullstat}`),
            request.get(`${baseUrl()}/others/${year}/${settlement_ekatte}/${bullstat}`),
            request.get(`${baseUrl()}/drawings/${year}/${settlement_ekatte}/${bullstat}`),
            request.get(`${baseUrl()}/companies/${bullstat}`),
            request.get(`${baseUrl()}/settlements/${settlement_ekatte}`),
            request.get(`${baseUrl()}/crops`),
            request.get(`${baseUrl()}/companies-initial-info/${year}/${settlement_ekatte}`)
        ])
            .then(([dataInit, dataDeals, dataOthers, dataDraw, dataCompany, dataSettlement,
                       dataCrops, dataSettlementCompanies]) => {
                setInitInfo(dataInit);
                setDealsInfo(dataDeals);
                setOthersInfo(dataOthers);
                setDrawInfo(dataDraw);
                setCompanyData(dataCompany);
                setCompanyTitle(`${dataCompany.company_name}, ЕИК: ${dataCompany.bullstat}`);
                setSettlementTitle(settlementLabel(dataSettlement));
                const crops_options = [];

                dataCrops.forEach(crop => {
                    crops_options.push({
                        id: crop.id,
                        value: crop.crop
                    });
                })
                setCropsOptions(crops_options);
                // Щ5: без самия ползвател — няма смисъл да се прехвърля на себе си.
                setSettlementCompanies(
                    (Array.isArray(dataSettlementCompanies) ? dataSettlementCompanies : [])
                        .filter(c => String(c.bullstat) !== String(bullstat))
                        .map(c => ({bullstat: c.bullstat, company_name: c.company_name}))
                        .sort((a, b) => String(a.company_name).localeCompare(String(b.company_name), 'bg')));
            })
            .catch(err => alert(err.message || 'Грешка при зареждане на данните.'))
    }, [year, settlement_ekatte, bullstat]);

    useEffect(() => {
        if (drawInfo) {
            setSumDrawings(drawInfo.reduce((sum, obj) => sum + obj.area, 0));
        }
    }, [drawInfo]);

    useEffect(() => {
        if (dealsInfo) {
            setSumDeals(dealsInfo.reduce((sum, obj) => sum + obj.amount, 0))
        }

        // Н0: сметката е ЕДНА — utils/pravno.js. Тук не се преписва.
        setSumToDrawing(pravno({
            suitableLand: initInfo.suitable_land,
            correction: othersInfo.correction,
            sumDeals: sumDeals,
            whiteSpots: othersInfo.white_spots,
            roads: othersInfo.roads,
            othersVal: othersInfo.others,
            othersPlus: othersInfo.others_plus,
        }));

    }, [dealsInfo, initInfo, sumDeals, othersInfo]);

    // В3 (пренесено в новата навигация): маркирай локалния таб „Други площи"
    // бледо червено, когато има въведена корекция. Стойността ѝ не се вижда
    // другаде на страницата на ползвателя.
    useEffect(() => {
        const correction = Number(othersInfo.correction) || 0;
        setTabMarker('company.other', correction !== 0
            ? {tone: 'danger', title: 'Въведена е корекция'} : null);
        return () => setTabMarker('company.other', null);
    }, [othersInfo.correction, setTabMarker]);

    // Н4: червен „!" на таба „БП и пътища", докато екранът не е минал през
    // човек. Признакът е „прегледано", НЕ „има стойност" — затова гасне и при
    // запис на нули, а липсващият ред за други площи също означава „не е
    // прегледано". Целта е да не се пропуска във работния процес.
    useEffect(() => {
        const прегледано = !!othersInfo.bp_roads_reviewed;
        setTabMarker('company.bproads', прегледано ? null : {
            tone: 'danger',
            badge: '!',
            title: 'Белите петна и пътищата още не са прегледани за този ползвател',
        });
        return () => setTabMarker('company.bproads', null);
    }, [othersInfo.bp_roads_reviewed, setTabMarker]);

    const onInitInfoSubmit = () => {
        request.get(`${baseUrl()}/companies-initial-info/${year}/${settlement_ekatte}/${bullstat}`)
            .then(data => {
                setInitInfo(data);
            })
            .catch(err => alert(err.message || 'Грешка при зареждане.'))
    }

    const onDealSubmit = () => {
        request.get(`${baseUrl()}/deals/${year}/${settlement_ekatte}/${bullstat}`)
            .then(data => {
                setDealsInfo(data);
            })
            .catch(err => alert(err.message || 'Грешка при зареждане.'))
    }

    const onOthersSubmit = () => {
        request.get(`${baseUrl()}/others/${year}/${settlement_ekatte}/${bullstat}`)
            .then(data => {
                setOthersInfo(data);
            })
            .catch(err => alert(err.message || 'Грешка при зареждане.'))
    }

    const onDrawingSubmit = () => {
        request.get(`${baseUrl()}/drawings/${year}/${settlement_ekatte}/${bullstat}`)
            .then(data => {
                setDrawInfo(data);
            })
            .catch(err => alert(err.message || 'Грешка при зареждане.'))
    }

    const onCompanySubmit = () => {
        request.get(`${baseUrl()}/companies/${bullstat}`)
            .then(data => {
                setCompanyData(data);
                setCompanyTitle(`${data.company_name}, ЕИК: ${data.bullstat}`);
            })
            .catch(err => alert(err.message || 'Грешка при зареждане.'))
    }

    return (
        <>
            {Object.keys(companyData).length !== 0 && <CompanyInfoModal info={companyData}/>}

            {/* Ъ12: обвивката пази мястото на фиксираните карти вдясно — иначе
                дълго име на землище се подпъхва под „Правно". */}
            <div className={styles.headRoom}>
                {settlementTitle && (
                    <div className="d-flex justify-content-center mt-3">
                        <span className="badge fs-5 px-4 py-2 text-wrap"
                              style={{backgroundColor: "var(--app-gold)"}}>
                            {settlementTitle}
                        </span>
                    </div>
                )}
                <h5 className="d-flex justify-content-center mt-3 mb-3 text-center"
                    style={{color: "var(--app-gold)"}}>{companyTitle}</h5>
            </div>
            <div className={styles.floatCards}>
                <ForDrawingCard sumToDrawing={sumToDrawing}/>
                <DrawingsSumCard sumDrawings={sumDrawings} sumToDrawing={sumToDrawing}/>
            </div>
            <InitInfo initInfo={initInfo} correction={othersInfo.correction} onInitInfoSubmit={onInitInfoSubmit}/>
            <OthersInfo othersInfo={othersInfo}/>
            <DealsInfo dealsInfo={dealsInfo} onDealDelete={onDealSubmit}/>
            <DrawingsInfo drawInfo={drawInfo} onDrawingEditDelete={onDrawingSubmit}
                          cropsOptions={cropsOptions} settlementCompanies={settlementCompanies}/>
            {/* Н6: котвата, до която бутоните „Редакция" превъртат. Нужна е,
                защото клик върху вече отворена форма не сменя маршрута и
                нейният собствен effect не се изпълнява втори път. */}
            <div id="company-forms">
            <Routes element={<RouteGuard/>}>
                <Route path="/deal" element={<DealNew onDealSubmit={onDealSubmit}/>}/>
                <Route path="/other"
                       element={<OtherNew bullstat={bullstat} settlement_ekatte={settlement_ekatte} year={year}
                                          onOthersSubmit={onOthersSubmit} othersInfo={othersInfo}/>}/>
                {/* Н4: новият таб „БП и пътища" — двата реда се преместиха тук. */}
                <Route path="/bp-roads"
                       element={<BpRoadsForm bullstat={bullstat} settlement_ekatte={settlement_ekatte} year={year}
                                             onOthersSubmit={onOthersSubmit} othersInfo={othersInfo}/>}/>
                <Route path="/drawing"
                       element={<DrawingNew bullstat={bullstat} settlement_ekatte={settlement_ekatte} year={year}
                                            onDrawingSubmit={onDrawingSubmit} sumToDrawing={sumToDrawing}
                                            sumDrawings={sumDrawings} cropsOptions={cropsOptions}/>}/>
                <Route path="/company"
                       element={<CompanyNew companyData={companyData} onCompanySubmit={onCompanySubmit}/>}/>
                <Route path="/copy"
                       element={<CopyFromPreviousYear onDealSubmit={onDealSubmit} onDrawingSubmit={onDrawingSubmit}
                                                      onOthersSubmit={onOthersSubmit}/>}/>
            </Routes>
            </div>
            <div id="div-to-scroll"></div>
        </>
    );
};
